import { ActionIcon, Checkbox, Flex, Table } from "@mantine/core";
import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { useStableCallback } from "@/hooks/useStableCallback";
import { SimpleColumnMapping } from "@/PageComponents/SimpleTable/SimpleTable";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS, useCombinedRefs } from "@dnd-kit/utilities";
import styles from "@/PageComponents/SectionTable/SectionTable.module.css";
import { TableAction, TableActionStates } from "@/PageComponents/Table/table-model";
import SectionTableSectionItem from "@/PageComponents/SectionTable/SectionTableSectionItem";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import DroppableRow from "@/PageComponents/SectionTable/DroppableRow";
import DraggableRow from "@/PageComponents/SectionTable/DraggableRow";
import SectionTableSectionHeading from "@/PageComponents/SectionTable/SectionTableSectionHeading";
import { IconCheck, IconChevronDown, IconMinus } from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";

export interface SectionTableSectionProps {
    group: { id: string, items: any[], isSection: boolean, [key: string]: any }
    index: number
    lastItem: boolean
    prevItemGroupId: string | undefined
    previousItemIsSection: boolean
    mapping: SimpleColumnMapping[]
    canEdit?: boolean
    controls?: TableAction[]
    sectionControls: any[]
    stylingProps?: {
        compact?: boolean
        rows?: boolean
        darkerText?: boolean
        clearHeader?: boolean
    }
    tableActionStates?: TableActionStates
    showControlsOnHover?: boolean
    tableItemInputMetadataByKeyName?: { [itemID: string]: { [itemValueKeyName: string]: { disabled: boolean; loading: boolean; error?: string | null } } }
    allowSections?: boolean
    lineClamp?: number
    useHideLineItemsPdfSetting?: boolean,
    useDisplaySubtotalsPdfSetting?: boolean,
    sectionHeaderDisplayValueFunction?: (group: any) => string | React.ReactNode
    headingSubtotal?: number
    // owned by SectionTable so the header can expand or collapse every section at once
    expanded: boolean
    onSetExpanded: (groupId: string, expanded: boolean) => void
    focusTitle?: boolean
    useSelectMode: boolean
    selectedItems: any[]
    itemsSelectedFilter?: (items: any) => boolean
    // callbacks: SectionTable passes stable identities so memoized sections and rows skip re-rendering
    onInputChange?: (name: string, item: any, value: number | '') => void
    onItemClicked?: (item: any, column: string) => void
    onAction?: (actionName: string, actionItem: any, actionItemIndex: number, group: any) => void
    onSectionItem: (actionName: string, group: any) => void
    onCreateNewSection: (item: any) => void
    onUpdateGroup: (newGroup: any) => void
    onClearGroup: (group: any) => void
    onItemsSelected: (items: any[]) => void
}

const SectionTableSection: FC<SectionTableSectionProps> = (props: any) => {

    // stable per section, so memoized rows don't re-render when this section does; reads the latest group and index
    const onRowAction = useStableCallback((actionName: string, item: any) => {
        props.onAction && props.onAction(actionName, item, props.index, props.group)
    })


    /*const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({
        id: props.group.id,
    })*/

    /*const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }*/


    const mobileView = useMediaQuery('(max-width: 1000px)', undefined, { getInitialValueInEffect: false })

    const expandItems = props.expanded
    const setExpandItems = (expanded: boolean) => props.onSetExpanded(props.group.id, expanded)

    const { attributes: draggableattributes, listeners: draggablelisteners, setNodeRef: draggablesetNodeRef, transform: draggabletransform, active }
        = useDraggable({
            id: props.group.id,
            data: {
                groupId: props.group.id,
                groupIndex: props.index,
                group: props.group,
                type: 'group-item'
            }
        });
    const draggablestyle = {
        transform: CSS.Translate.toString(draggabletransform),
    };

    const [hoverOverCount, setHoverOverCount] = useState(0)
    const [temporaryExpandedState, setTemporaryExpandedState] = useState(false)

    useEffect(() => {
        if (active && expandItems) {
            if (active.id === props.group.id) {
                setExpandItems(false)
            }
        }/* else if (!active && hoverOverCount > 2) {
            setExpandItems(true)
        }*/
    }, [active]);


    const { setNodeRef: droppableNodeRef, over } = useDroppable({
        id: props.group.id,
        disabled: (!props.group.isSection || active?.data.current?.group?.isSection || active?.data.current?.groupId === props.group.id) && active?.id !== props.group.id /*!props.group.isSection*//* true*/,
        data: {
            groupId: props.group.id,
            groupIndex: props.index,
            group: props.group,
            type: 'group-item',
        }
    })

    // counts half-seconds a drag hovers this section so it opens to show where the line will land. The timer lives
    // only while hovered: Mantine's useInterval restarted on every render and could leave a timer running after the
    // drop, which kept the count above 0 and the section open no matter what its toggle said
    const hovered = !!over && over.data.current?.groupId === props.group.id
    useEffect(() => {
        if (!hovered) return
        const timer = setInterval(() => setHoverOverCount((p: number) => p + 1), 500)
        return () => clearInterval(timer)
    }, [hovered]);

    useEffect(() => {
        if (hovered) return
        if (!over && hoverOverCount) {
            setExpandItems(true)
        }
        setHoverOverCount(0)
    }, [over]);

    const combinedNodes = useCombinedRefs(/*setNodeRef, */draggablesetNodeRef, droppableNodeRef)

    const checked = useMemo(() => {

        if (props.itemsSelectedFilter) {
            return props.useSelectMode && props.group.items && props.group.items.filter(props.itemsSelectedFilter).length !== 0 &&
                props.group.items.filter((x: any) => props.itemsSelectedFilter ? props.itemsSelectedFilter(x) : true).every((x: any) => props.selectedItems.findIndex((y: any) => x.ID === y.ID) !== -1);
        }

        return props.useSelectMode && props.group.items && props.group.items.length !== 0 &&
            props.group.items.every((x: any) => props.selectedItems.findIndex((y: any) => x.ID === y.ID) !== -1);
    }, [props.group.items, props.selectedItems]);

    const indeterminate = useMemo(() => {
        return props.useSelectMode && !checked &&
            props.group.items.some((x: any) => props.selectedItems.findIndex((y: any) => x.ID === y.ID) !== -1);
    }, [checked, props.selectedItems]);

    const handleGroupCheckChange = (e: any) => {
        props.onItemsSelected(
            e.currentTarget.checked ? [
                ...props.selectedItems,
                ...props.group.items.filter((x: any) => !props.selectedItems.some((y: any) => x.ID === y.ID))
            ] : [
                ...props.selectedItems.filter((x: any) => !props.group.items.some((y: any) => y.ID === x.ID))
            ]
        )
    }

    const handleItemCheckChange = (item: any) => {
        props.onItemsSelected(
            props.selectedItems.some((x: any) => x.ID === item.ID) ? props.selectedItems.filter((x: any) => x.ID !== item.ID) : [...props.selectedItems, item]
        )
    }

    const disabledSelect = props.group.isSection ? () => false : () => {
        var item = props.group.items[0];
        return disableItemSelect(item);
    };

    const disableItemSelect = (item: any) => {
        if (props.itemsSelectedFilter) {
            return !props.itemsSelectedFilter(item);
        }
        return false;
    }

    const renderItem = (item: any) => (
        <SectionTableSectionItem
            item={item}
            isSection={props.group.isSection}
            groupIndex={props.index}
            mapping={props.mapping}
            canEdit={props.canEdit}
            controls={props.controls}
            stylingProps={props.stylingProps}
            tableActionStates={props.tableActionStates}
            showControlsOnHover={props.showControlsOnHover}
            inputMetadata={props.tableItemInputMetadataByKeyName?.[item.ID]}
            allowSections={props.allowSections}
            lineClamp={props.lineClamp}
            onInputChange={props.onInputChange}
            onItemClicked={props.onItemClicked}
            onRowAction={onRowAction}
            onCreateNewSection={props.onCreateNewSection}
        />
    )

    return (
        <>

            <DroppableRow id={'section-above-' + props.index}
                data={{
                    type: 'group-above',
                    groupIndex: props.index,
                    // groupId: props.group.id,
                }}
                colspan={props.mapping.length + 2}
                disabled={active?.id === props.group.id || active?.id === props.prevItemGroupId}
                groupId={props.group.id}
                sectionGroupBorder={props.group.isSection || props.previousItemIsSection}
            />


            {

                // master group row

                <tr
                    onDragEnd={() => setExpandItems(true)}
                    className={props.group.isSection && styles.sectionHeading || ''}
                    ref={combinedNodes}
                    style={{
                        ...draggablestyle,
                        ...(active?.id === props.group.id ? {
                            opacity: '.6'
                        } : {}),
                        ...((over?.id === props.group.id || over?.data.current?.groupId === props.group.id) && active?.data.current?.groupId !== props.group.id && {
                            backgroundColor: 'var(--mantine-color-scBlue-0)',
                        }),
                        cursor: props.group.isSection ? 'pointer' : 'default'
                    }}
                    onClick={() => setExpandItems(!expandItems)}
                /*onMouseEnter={() => {
                    // console.log('mouse enter', props.group.id,  over?.id, over?.data.current?.groupId )
                    // if((over?.id === props.group.id || over?.data.current?.groupId === props.group.id) && active?.data.current?.groupId !== props.group.id) {
                    /!*if(!!active && active?.data.current?.groupId !== props.group.id) {
                        // console.log('start interval')
                        interval.start()
                    }*!/
                    // over?.id === props.group.id /!*&& active?.id !== props.group.id*!/ &&
                }}
                onMouseLeave={() => {
                    /!*if(interval.active) {
                        setHoverOverCount(0)
                        interval.stop()
                    }*!/
                }}*/
                >
                    <td style={{ width: 40 }}>
                        <Flex pos={'relative'} align={'center'}>
                            {
                                props.group.isSection &&
                                <Flex
                                    className={styles.sectionChevron}
                                >
                                    <ActionIcon
                                        variant={'transparent'}
                                        c={'gray'}
                                        ml={mobileView ? 7 : 0}
                                        aria-label={expandItems ? 'Collapse section' : 'Expand section'}
                                        aria-expanded={expandItems}
                                    >
                                        <IconChevronDown className={styles.ease + ' ' + (!expandItems ? styles.rotate90 : '')} size={'1em'} />
                                    </ActionIcon>
                                </Flex>
                            }

                            {
                                props.useSelectMode && <>
                                    <Checkbox
                                        onClick={(e: any) => e.stopPropagation()}
                                        mx={'auto'}
                                        ml={10}
                                        size={'xs'}
                                        color={'scBlue'}
                                        checked={checked}
                                        indeterminate={indeterminate}
                                        onChange={handleGroupCheckChange}
                                        disabled={disabledSelect()}
                                        icon={
                                            ({ indeterminate, className }: any) =>
                                                indeterminate ? <IconMinus className={className} strokeWidth={4} /> :
                                                    <IconCheck className={className} strokeWidth={4} />
                                        }
                                    /*icon={
                                        ({ indeterminate, className }) =>
                                            indeterminate ? <IconMinus className={className} strokeWidth={4} /> : <IconListCheck className={className} strokeWidth={4} />
                                    }*/
                                    />
                                </>
                            }

                            <Flex
                                align={'center'} className={mobileView ? '' : styles.dragHandle} c={'gray'}
                                ml={10}
                            >
                                {
                                    (props.canEdit ?? true) &&
                                    <ActionIcon variant={'transparent'} style={{ cursor: 'grab' }}
                                        {...((props?.canEdit ?? true) && draggableattributes)}
                                        {...((props?.canEdit ?? true) && draggablelisteners)}
                                    >
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill={'#afafaf'} height="16"
                                                width="14"
                                                viewBox="0 0 448 512"
                                                onClick={(e: any) => {
                                                    e.stopPropagation()
                                                    setExpandItems(!expandItems)
                                                }}
                                            >
                                                <path
                                                    d="M32 288c-17.7 0-32 14.3-32 32s14.3 32 32 32l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L32 288zm0-128c-17.7 0-32 14.3-32 32s14.3 32 32 32l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L32 160z" />
                                            </svg>
                                        </>
                                    </ActionIcon>
                                }
                            </Flex>
                        </Flex>
                    </td>

                    {
                        !props.group.isSection ?
                            renderItem(props.group.items[0])
                            :
                            <SectionTableSectionHeading
                                sectionHeaderDisplayValueFunction={props.sectionHeaderDisplayValueFunction}
                                mainColumnSpan={props.mapping.length}
                                group={props.group}
                                onClearGroup={props.onClearGroup}
                                sectionControls={props.sectionControls}
                                onSectionItem={props.onSectionItem}
                                useHideLineItemsPdfSetting={props.useHideLineItemsPdfSetting}
                                useDisplaySubtotalsPdfSetting={props.useDisplaySubtotalsPdfSetting}
                                onUpdateGroup={props.onUpdateGroup}
                                canEdit={props.canEdit}
                                allowSections={props.allowSections}
                                headingSubtotal={props.headingSubtotal}
                                focusTitle={props.focusTitle}
                                showSubtotal={typeof props.headingSubtotal === "number" && props.group.items.some((x: any) => typeof x.LineTotalExclusive === "number")}
                            />
                    }
                </tr>
            }

            {
                (expandItems || (hoverOverCount > 0 && props.group.id !== active?.id)) &&
                props.group.isSection &&
                <SortableContext
                    items={props.group.items.map((x: any) => ({ id: x.ID, ...x })) as any} // need to add a prop called id for dnd to work
                    strategy={verticalListSortingStrategy}
                >
                    {
                        props.group.items.map((x: any, i: any) => (
                            <DraggableRow
                                id={x.ID}
                                key={x.ID + 'row'}
                                colspan={props.mapping.length + 2}
                                lastItem={props.group.items.length === i + 1}
                                groupId={props.group.id}
                                groupIndex={props.index}
                                itemIndex={i}
                                group={props.group}
                                disableDrag={!!active && active.data.current?.groupId !== props.group.id}
                                canEdit={props.canEdit}
                                useSelectMode={props.useSelectMode}
                                selectedItems={props.selectedItems}
                                onItemCheckChange={() => handleItemCheckChange(x)}
                                disableSelect={disableItemSelect(x)}
                            >
                                {renderItem(x)}
                            </DraggableRow>
                        ))
                    }
                </SortableContext>
            }

            {
                props.lastItem &&
                <DroppableRow id={'section-below-' + props.index}
                    data={{
                        type: 'group-below',
                        groupIndex: props.index,
                        // groupId: props.group.id,
                    }}
                    disabled={active?.id === props.group.id}
                    colspan={props.mapping.length + 2}
                    groupId={props.group.id}
                    sectionGroupBorder={props.group.isSection}
                />
            }

        </>
    );
}

export default memo(SectionTableSection)
